import { createFileRoute, Link } from "@tanstack/react-router";
import { PageShell, Card } from "@/components/page-shell";
import { BiteFlow } from "@/components/bite/bite-flow";
import { SourceList } from "@/components/source-list";
import { Faq, faqJsonLd } from "@/components/faq";
import { breadcrumbJsonLd, howToJsonLd } from "@/lib/jsonld";
import { removalHowToSteps } from "@/lib/removal";
import { absUrl, ogImageUrl } from "@/lib/site";
import { pageTitle } from "@/lib/seo";

const FAQ = [
  {
    q: "What's the safest way to remove a tick?",
    a: "Use fine-tipped tweezers to grasp the tick close to the skin and pull steadily upward without twisting. Then clean the area. Avoid folk methods like burning the tick or using petroleum jelly.",
  },
  {
    q: "Part of it broke off in my skin. What now?",
    a: "Try to lift the remaining mouthparts out with tweezers. If they don't come away easily, leave them and let the skin heal. Digging after them does more harm than they do.",
  },
  {
    q: "Should I see a doctor after every tick bite?",
    a: "Most bites don't cause illness. Watch for symptoms and contact a licensed healthcare professional if a rash, fever, fatigue, or delayed meat reaction develops. Seek emergency care for severe allergic symptoms.",
  },
  {
    q: "How long do I need to watch for symptoms?",
    a: "It depends on the illness. Published windows on this site run from about 1 day to 30 days after a bite, and a few conditions have no firm window at all. The timeline on this page counts them from the date you enter. A window closing is not an all-clear.",
  },
  {
    q: "Is my bite log shared with anyone?",
    a: "No. The log and any photo you add are stored only on your device and are never uploaded. You can download or print the record to share with a healthcare professional yourself.",
  },
];

/** Where to go once the tick is out. Was three cards, each with an icon. */
const NEXT = [
  {
    title: "Which tick was it?",
    body: "Narrow it down by size and markings. It shortens the watch list above.",
    to: "/tick-identification",
    link: "Identify a tick",
  },
  {
    title: "What each illness looks like",
    body: "Symptoms, onset windows and treatment for every tickborne illness we cover.",
    to: "/diseases",
    link: "Tickborne illnesses",
  },
  {
    title: "Next time",
    body: "Repellents, clothing and the tick check that catches nymphs.",
    to: "/prevention",
    link: "Prevention",
  },
] as const;

const OG = ogImageUrl({
  title: "What to do right now after a tick bite",
  subtitle: "Remove it, log it, know what to watch for",
  kind: "tickcheck",
});

export const Route = createFileRoute("/tick-bite-tool")({
  head: () => ({
    meta: [
      { title: pageTitle("Tick Bite Tool: Remove It, Log It, Know What to Watch") },
      {
        name: "description",
        content:
          "Found a tick? Remove it step by step, log the bite privately on your device, and get a symptom timeline counted from the day you were bitten.",
      },
      { property: "og:title", content: "What to do right now after a tick bite" },
      {
        property: "og:description",
        content:
          "Remove it, log it on your device, and see what to watch for and when. Free, no signup.",
      },
      { property: "og:url", content: absUrl("/tick-bite-tool") },
      { property: "og:type", content: "article" },
      { property: "og:image", content: OG },
      { name: "twitter:title", content: "What to do right now after a tick bite" },
      { name: "twitter:image", content: OG },
    ],
    links: [{ rel: "canonical", href: absUrl("/tick-bite-tool") }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(faqJsonLd(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          howToJsonLd({
            name: "How to remove an attached tick",
            description:
              "Remove an attached tick safely with fine-tipped tweezers, clean the bite, and keep a record of when and where it happened.",
            path: "/tick-bite-tool",
            steps: removalHowToSteps(),
          }),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Tick bite tool", path: "/tick-bite-tool" },
          ]),
        ),
      },
    ],
  }),
  component: TickBiteTool,
});

function TickBiteTool() {
  return (
    <PageShell
      crumbs={[
        { name: "Home", path: "/" },
        { name: "Tick bite tool", path: "/tick-bite-tool" },
      ]}
      title="I found a tick attached. What do I do?"
      intro="Stay calm. Most tick bites don't lead to illness. Take it out, write down the date, and this page will tell you what to watch for and for how long."
    >
      <BiteFlow
        lead={
          <section
            aria-labelledby="emergency-first"
            className="border border-l-[3px] border-border border-l-destructive bg-card px-5 py-4 sm:px-6 sm:py-5"
          >
            <h2 id="emergency-first" className="label-caps text-destructive">
              Emergency first
            </h2>
            <p className="mt-1.5 text-[0.9375rem] leading-relaxed text-foreground">
              If you or someone has trouble breathing, throat or tongue swelling, fainting, or a
              severe whole-body reaction,{" "}
              <strong className="font-semibold">call emergency services (911) right away</strong>.
              Don&rsquo;t wait.
            </p>
          </section>
        }
      />

      <div data-print-region="extras" className="no-print grid gap-x-8 gap-y-2 sm:grid-cols-3">
        {NEXT.map((n) => (
          <Card key={n.to} padding="sm">
            <h2 className="font-display text-xl font-medium leading-snug text-foreground">
              {n.title}
            </h2>
            <p className="mt-1.5 text-[0.9375rem] leading-relaxed text-muted-foreground">
              {n.body}
            </p>
            <Link to={n.to} className="link mt-2 inline-block text-sm font-semibold">
              {n.link}
            </Link>
          </Card>
        ))}
      </div>

      {/* A printout of this page is the bite record for a clinician; the FAQ and a
          collapsed source list are screen furniture. */}
      <Card className="no-print">
        <h2 className="font-display text-[1.75rem] font-medium leading-tight text-foreground">
          Common questions
        </h2>
        <div className="mt-5">
          <Faq items={FAQ} />
        </div>
      </Card>

      <div className="no-print">
        <SourceList
          ids={["cdc-remove-tick", "cdc-prevent-bites", "cdc-tickborne", "cdc-ags"]}
          title="Where this comes from"
          collapsible
        />
      </div>

      <p
        data-print-region="extras"
        className="measure text-sm leading-relaxed text-muted-foreground"
      >
        Educational information only. Not a diagnosis or treatment plan. The timeline on this page
        restates published onset windows against the date you entered; it does not assess your
        symptoms or your risk. For symptoms or medical questions, contact a licensed healthcare
        professional. See the full{" "}
        <Link to="/tick-bite-guide" className="link">
          tick bite guide
        </Link>
        ,{" "}
        <Link to="/alpha-gal" className="link">
          alpha-gal guide
        </Link>
        , and{" "}
        <Link to="/disclaimer" className="link">
          disclaimer
        </Link>
        .
      </p>
    </PageShell>
  );
}
